import {
  faAlarmClock,
  faCheck,
  faCircleInfo,
  faPlus,
  faQuestionCircle,
  faShieldCheck,
} from '@fortawesome/pro-regular-svg-icons'
import { Box, Button, Flex, Loader, Menu, Text, Tooltip } from '@mantine/core'
import { isToday } from 'date-fns'
import { useRouter } from 'next/router'
import { useContext, useState } from 'react'

import { useAnalytics } from '@flecto/analytics'
import { useMutationCreateInsurance } from '@flecto/core/saas'
import { useTranslation } from '@flecto/translations'
import { type CoreInsurance, type CoreInsurancePayload } from '@flecto/types'
import {
  Alert,
  CardList,
  Modal,
  ModalsContext,
  TextCurrency,
  TextDate,
  useNotifications,
} from '@flecto/ui'
import { Icon } from '@flecto/ui'
import { formatDateString } from '@flecto/utils'
import { useTimeUntil } from '@flecto/utils/src/Hooks/useTimeUntil'

import useInsuranceState from '../../../insurances/hooks/useInsuranceState'
import styles from './styles.module.css'

export type ModalConfirmationCreateInsuranceProps = {
  form: CoreInsurancePayload & {
    customer: CoreInsurance['customer']
    item: CoreInsurance['item']
    start_at_now: boolean
    price_cents: number
  }
}

export function ModalConfirmationCreateInsurance({
  form,
}: ModalConfirmationCreateInsuranceProps) {
  const { t: tActions } = useTranslation('shared', { keyPrefix: 'actions' })
  const { t } = useTranslation('saas', { keyPrefix: 'insurances' })
  const [responseData, setResponseData] = useState<CoreInsurance | null>(null)
  const { closeModal } = useContext(ModalsContext)
  const { showNotification } = useNotifications()
  const { setLastUpdate } = useInsuranceState()

  const startIn = useTimeUntil(form.start_at as string)

  const track = useAnalytics('segment')
  const router = useRouter()

  const {
    mutateAsync: createInsurance,
    isLoading,
    isSuccess,
  } = useMutationCreateInsurance()

  const rows = [
    {
      label: t('insurances_details.label_customer'),
      value: form?.customer?.full_name ?? '-',
    },
    {
      label: t('table.label_start_date'),
      value: form?.start_at ?? '-',
    },
    {
      label: t('table.label_end_date'),
      value: form?.end_at ?? '-',
    },
    {
      label: t('table.label_product'),
      value: form?.item?.title ?? '-',
    },
    {
      label: t('insurances_details.label_product_quantity'),
      value: form?.quantity ?? '-',
    },
    {
      label: t('create_insurance.insurance_details.est_coverage_cost'),
      value: form?.price_cents ?? '-',
    },
  ]

  const handleSave = async () => {
    try {
      const response = await createInsurance({
        insurance: {
          customer_id: form?.customer?.id ?? '',
          end_at: form?.end_at ?? '',
          start_at: form.start_at_now ? undefined : (form?.start_at ?? ''),
          item_id: form?.item_id ?? '',
          notes: form?.notes,
          quantity: form?.quantity ?? 0,
          incidence_value_cents: form?.incidence_value_cents ?? 0,
        },
        start_now: form?.start_at_now ?? undefined,
      })

      setResponseData(response)

      await router.push(`/insurances/${response?.internal_id ?? ''}`)
    } catch {
      closeModal()
      showNotification({
        type: 'error',
        title: t('create_insurance.modal_confirmation.error_title'),
        message:
          t('create_insurance.modal_confirmation.error_description') ?? '',
      })
    }
  }

  const renderStartOrEndDate = (label: string, value: string | number) => {
    if (value === '-') return '-'

    if (
      label === t('table.label_start_date') &&
      form.start_at_now &&
      isToday(new Date(value.toString().substring(0, 24)))
    ) {
      return (
        <Text c='orange.5' fw={500}>
          {t('create_insurance.modal_confirmation.start_now')}
        </Text>
      )
    }

    return (
      <Text>
        {formatDateString({
          date: String(value).substring(0, 24),
          variant: 'datetime-brief',
        })}
      </Text>
    )
  }

  const renderCoverageCost = (value: number | string) => {
    if (value === '-') return '-'

    return <TextCurrency cents={(value as number) ?? 0} fw={500} />
  }

  const renderItemValue = (label: string, value: string | number) => {
    if (
      label === t('table.label_start_date') ||
      label === t('table.label_end_date')
    ) {
      return renderStartOrEndDate(label, value)
    }

    if (label === t('create_insurance.insurance_details.est_coverage_cost')) {
      return renderCoverageCost(value)
    }

    return <Text>{value ?? ''}</Text>
  }

  const handleFromScratch = async () => {
    closeModal()

    await router.push({
      pathname: '/insurances/create',
      query: {
        from_scratch: true,
      },
    })

    track('INSURANCE__CREATE_NEW', {
      type: 'scratch',
    })
  }

  const handleUseSameCustomerAndDates = async () => {
    closeModal()

    setLastUpdate(
      responseData?.customer as CoreInsurance['customer'],
      responseData?.start_at as CoreInsurance['start_at'],
      responseData?.end_at as CoreInsurance['end_at']
    )

    track('INSURANCE__CREATE_NEW', {
      type: 'same_data',
    })

    await router.push({
      pathname: '/insurances/create',
      query: {
        use_same_customer_and_dates: true,
      },
    })
  }

  return (
    <Modal size='md'>
      {!isLoading && (
        <Modal.Icon icon={isSuccess ? faCheck : faShieldCheck} color='green' />
      )}
      <Modal.Title>
        {isSuccess
          ? t('create_insurance.modal_confirmation.success_title', {
              id: responseData?.internal_id,
            })
          : isLoading
            ? t('create_insurance.modal_confirmation.loading_title')
            : t('create_insurance.modal_confirmation.confirm_policy_title')}
      </Modal.Title>
      <Modal.Content>
        {isLoading ? (
          <Flex direction='column' gap='sm' align='center'>
            <Loader c='dimmed' />
            <Text>
              {t('create_insurance.modal_confirmation.loading_description')}
            </Text>
          </Flex>
        ) : isSuccess ? (
          <Flex gap='xs' wrap='wrap' c='dimmed'>
            <Text>{responseData?.quantity}x</Text>
            <Text fw={500}>{responseData?.item?.title}</Text>
            <Text>
              {t('create_insurance.modal_confirmation.success_description')}
            </Text>
            <Text fw={500}>{responseData?.customer?.full_name}</Text>
            <Text>
              {t(
                'create_insurance.modal_confirmation.success_description_from'
              )}
            </Text>
            <TextDate
              date={responseData?.start_at ?? ''}
              variant='datetime-brief'
              fw={500}
            />
            <Text>
              {t('create_insurance.modal_confirmation.success_description_to')}
            </Text>
            <TextDate
              date={responseData?.end_at ?? ''}
              variant='datetime-brief'
              fw={500}
            />
            .
            {!form.start_at_now && (
              <Text>
                {t(
                  'create_insurance.modal_confirmation.success_description_start_in',
                  {
                    date: startIn,
                  }
                )}
              </Text>
            )}
          </Flex>
        ) : (
          <Flex gap='md' direction='column'>
            {form.start_at_now ? (
              <Alert
                variant='warning'
                icon={<Icon icon={faAlarmClock} color='orange' />}
                title={t('create_insurance.modal_confirmation.alert_title')}
                description={t(
                  'create_insurance.modal_confirmation.alert_description'
                )}
              />
            ) : (
              <Alert
                variant='info'
                icon={<Icon icon={faAlarmClock} color='blue' />}
                title={t(
                  'create_insurance.modal_confirmation.alert_title_start_in',
                  {
                    date: startIn,
                  }
                )}
                description={t(
                  'create_insurance.modal_confirmation.alert_description_start_in'
                )}
              />
            )}

            <CardList w='100%' h='100%'>
              {rows.map((row) => (
                <CardList.Item key={row?.label} className={styles.cardListItem}>
                  <CardList.ItemLabel className={styles.cardListItemLabel}>
                    {row.label ===
                    t(
                      'create_insurance.insurance_details.est_coverage_cost'
                    ) ? (
                      <Flex gap='xs' align='center'>
                        <Text>{row.label}</Text>
                        <Tooltip
                          position='right'
                          label={t(
                            'create_insurance.modal_confirmation.tooltip_label'
                          )}
                        >
                          <Box>
                            <Icon
                              icon={faQuestionCircle}
                              color='dimmed'
                              size='xs'
                            />
                          </Box>
                        </Tooltip>
                      </Flex>
                    ) : (
                      row.label
                    )}
                  </CardList.ItemLabel>
                  <CardList.ItemValue className={styles.cardListItemValue}>
                    {renderItemValue(row.label, row.value)}
                  </CardList.ItemValue>
                </CardList.Item>
              ))}
              <Flex
                mt='md'
                style={{
                  borderTop: '1px solid var(--mantine-color-gray-2)',
                }}
                w='100%'
                py='md'
              >
                <Flex px='lg' gap='xs' align='center' wrap='wrap'>
                  <Icon icon={faCircleInfo} color='gray.7' mr='xs' />
                  <Text c='gray.7'>
                    {t('create_insurance.modal_confirmation.card_description')}
                  </Text>
                </Flex>
              </Flex>
            </CardList>
          </Flex>
        )}
      </Modal.Content>

      {!isLoading && !isSuccess && <Modal.Cancel />}

      {!isLoading && !isSuccess && (
        <Modal.Submit onClick={() => handleSave()} preventClose>
          {tActions('save')}
        </Modal.Submit>
      )}

      {isSuccess && (
        <Modal.Submit
          variant='default'
          onClick={() => void router.push('/insurances')}
        >
          {t('create_insurance.modal_confirmation.back_to_table_btn')}
        </Modal.Submit>
      )}

      {isSuccess && (
        <Modal.FooterExtra>
          <Menu withinPortal zIndex={9999}>
            <Menu.Target>
              <Button
                variant='subtle'
                size='md'
                leftSection={<Icon icon={faPlus} size='md' />}
              >
                {t('create_insurance.modal_confirmation.btn_create_another')}
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                onClick={(e: React.MouseEvent) => {
                  handleFromScratch()
                  e.stopPropagation()
                }}
              >
                {t('create_insurance.modal_confirmation.btn_from_scratch')}
              </Menu.Item>
              <Menu.Item
                onClick={(e: React.MouseEvent) => {
                  handleUseSameCustomerAndDates()
                  e.stopPropagation()
                }}
              >
                {t(
                  'create_insurance.modal_confirmation.btn_use_same_customer_and_dates'
                )}
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Modal.FooterExtra>
      )}
    </Modal>
  )
}
